import * as DialogPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"
import { Maximize2, Minimize2, X } from "lucide-react"
import * as React from "react"

import { IconButton } from "@/components/ui/icon-button"
import { cn } from "@/lib/utils"

/**
 * The Sheet root. Pass `modal={false}` when the sheet is a companion panel
 * (findings drawer next to a live PDF, for instance) so the page underneath
 * stays interactive — pair with `scrim={false}` on SheetContent. Defaulting
 * to modal={true} keeps the common case (confirm-style drawer) safe.
 */
export const Sheet = DialogPrimitive.Root
export const SheetTrigger = DialogPrimitive.Trigger
export const SheetClose = DialogPrimitive.Close

const sheetContent = cva(
  [
    "fixed z-drawer flex flex-col gap-0 bg-surface shadow-e3",
    "data-[state=open]:animate-in data-[state=closed]:animate-out",
    "duration-260 ease-out motion-reduce:animate-none",
  ],
  {
    variants: {
      side: {
        right: [
          "inset-y-0 right-0 h-full border-l border-border",
          "data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right",
        ],
        left: [
          "inset-y-0 left-0 h-full border-r border-border",
          "data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left",
        ],
        bottom: [
          "inset-x-0 bottom-0 rounded-t-xl border-t border-border",
          "data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
        ],
      },
      size: {
        sm: "w-[min(360px,92vw)]",
        md: "w-[min(420px,92vw)]",
        lg: "w-[min(560px,94vw)]",
      },
    },
    defaultVariants: { side: "right", size: "md" },
  },
)

/**
 * Class variant for the "maximized" state — the sheet promotes to a
 * centered modal card. Wider than the docked variant, capped at ~1000px
 * so text lines don't stretch past readable width, tall but not full
 * height so the workspace behind it stays legible around the edges.
 */
const sheetModal = cn(
  "fixed z-modal left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col",
  "w-[min(1040px,94vw)] h-[min(88vh,900px)]",
  "rounded-2xl border border-border bg-surface shadow-e3",
  "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
  "duration-200 ease-out motion-reduce:animate-none",
)

export interface SheetContentProps
  extends
    Omit<React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>, "title">,
    VariantProps<typeof sheetContent> {
  title: React.ReactNode
  /** One line under the title. Say what the drawer is for. */
  description?: React.ReactNode
  /** Rendered in the header, before the close button. */
  headerAction?: React.ReactNode
  /** Pinned to the bottom, outside the scroll area. */
  footer?: React.ReactNode
  /** Dim the page behind. Off for drawers over a live document. */
  scrim?: boolean
  /**
   * When true the header carries a maximize toggle: the sheet grows from
   * a side drawer into a centered modal card, and shrinks back on the
   * inverse gesture. Preference persists per `title` in localStorage.
   *
   * The bar for enabling this is "the reviewer will spend real time in
   * here" — not "why not". A cheap glance panel should stay small so
   * the workspace behind it doesn't get obscured for no reason.
   */
  maximizable?: boolean
  /**
   * Start maximized (centered modal) on first open — useful for flows
   * that need focus but benefit from being minimizable to a side rail
   * (e.g. a wizard the reviewer may want to park while checking a value
   * on the queue behind).
   */
  defaultMaximized?: boolean
}

export const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(function SheetContent(
  {
    className,
    side,
    size,
    title,
    description,
    headerAction,
    footer,
    scrim = true,
    maximizable = false,
    defaultMaximized = false,
    children,
    ...props
  },
  ref,
) {
  const storageKey = React.useMemo(
    () => `pyxis:sheet-max:${typeof title === "string" ? title : "panel"}`,
    [title],
  )
  const [maximized, setMaximized] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return defaultMaximized
    try {
      const stored = window.localStorage.getItem(storageKey)
      return stored === null ? defaultMaximized : stored === "1"
    } catch {
      return defaultMaximized
    }
  })

  const toggleMaximized = React.useCallback(() => {
    setMaximized((prev) => {
      const next = !prev
      try {
        window.localStorage.setItem(storageKey, next ? "1" : "0")
      } catch {
        /* private mode or quota — accept ephemeral toggle */
      }
      return next
    })
  }, [storageKey])

  return (
    <DialogPrimitive.Portal>
      {scrim && (
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-drawer bg-[rgb(18_22_28/0.35)]",
            maximized && "bg-[rgb(18_22_28/0.55)] backdrop-blur-sm",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
            "duration-260 motion-reduce:animate-none",
          )}
        />
      )}
      <DialogPrimitive.Content
        ref={ref}
        className={cn(maximized ? sheetModal : sheetContent({ side, size }), className)}
        {...props}
      >
        <header className="flex shrink-0 items-center gap-2.5 border-b border-border px-4 py-3.5">
          <div className="min-w-0 flex-1">
            <DialogPrimitive.Title className="text-heading">{title}</DialogPrimitive.Title>
            {description ? (
              <DialogPrimitive.Description className="mt-0.5 text-caption leading-normal text-text-3">
                {description}
              </DialogPrimitive.Description>
            ) : (
              // Radix warns without one; the title carries the meaning here
              <DialogPrimitive.Description className="sr-only">
                {typeof title === "string" ? title : "Panel"}
              </DialogPrimitive.Description>
            )}
          </div>
          {headerAction}
          {maximizable && (
            <IconButton
              size="sm"
              variant="ghost"
              label={maximized ? "Restore panel" : "Expand to full view"}
              icon={maximized ? <Minimize2 /> : <Maximize2 />}
              onClick={toggleMaximized}
              noTooltip
            />
          )}
          <DialogPrimitive.Close asChild>
            <IconButton size="sm" variant="ghost" label="Close" icon={<X />} noTooltip />
          </DialogPrimitive.Close>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

        {footer && <div className="shrink-0 border-t border-border bg-surface p-3.5">{footer}</div>}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
})

/** A titled block inside a sheet. Keeps the padding consistent. */
export function SheetSection({
  title,
  description,
  children,
  className,
}: {
  title?: React.ReactNode
  description?: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  return (
    <section className={cn("border-b border-border-soft p-4 last:border-b-0", className)}>
      {title && (
        <h3 className="font-id text-eyebrow uppercase tracking-[0.14em] text-text-3">{title}</h3>
      )}
      {description && <p className="mt-1 text-caption leading-normal text-text-3">{description}</p>}
      <div className={cn(title && "mt-2.5")}>{children}</div>
    </section>
  )
}
